import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { DataGridComponent, GridAction, GridColumn, GridConfig } from '../../shared/data-grid/data-grid.component';
import { WellAttachmentsService, WellAttachment } from '../../services/well-attachments.service';
import { DeleteConfirmationDialogComponent } from './delete-confirmation-dialog/delete-confirmation-dialog.component';
import { EditDescriptionDialogComponent, EditDescriptionDialogResult } from './edit-description-dialog/edit-description-dialog.component';
import { UploadAttachmentsDialogComponent } from './upload-attachments-dialog/upload-attachments-dialog.component';

export interface WellAttachmentsDialogData {
  wellId: number;
  wellName: string;
}

@Component({
  selector: 'app-well-attachments-dialog',
  standalone: true,
  imports: [CommonModule, MaterialModule, DataGridComponent],
  templateUrl: './well-attachments-dialog.component.html',
  styleUrls: ['./well-attachments-dialog.component.css']
})
export class WellAttachmentsDialogComponent implements OnInit {
  attachments: WellAttachment[] = [];
  isLoading = true;
  isUploading = false;
  isDownloading = false;
  uploadError = '';

  columns: GridColumn[] = [
    { key: 'fileName', label: 'Document Name', sortable: true, filterable: true, width: '150px' },
    { key: 'uploadedBy', label: 'Uploaded By', sortable: true, filterable: true, width: '220px' },
    { key: 'description', label: 'Description', sortable: true, filterable: true, width: '280px' },
  ];

  actions: GridAction[] = [
    { label: 'Open', color: 'secondary' },
    { label: 'Edit', color: 'secondary' },
    { label: 'Delete', color: 'danger' },
  ];

  config: GridConfig = {
    pageSize: 5,
    enableSorting: true,
    stickyHeader: true,
    showColumnFilters: false,
  };

  private readonly wellAttachmentsService = inject(WellAttachmentsService);
  private readonly dialog = inject(MatDialog);

  constructor(
    public dialogRef: MatDialogRef<WellAttachmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WellAttachmentsDialogData
  ) { }

  ngOnInit(): void {
    this.loadAttachments();
  }

  onActionClicked(event: { action: GridAction; item: WellAttachment }): void {
    switch (event.action.label) {
      case 'Open':
        this.openAttachment(event.item);
        break;
      case 'Edit':
        this.editDescription(event.item);
        break;
      case 'Delete':
        this.deleteAttachment(event.item);
        break;
    }
  }

  private openAttachment(attachment: WellAttachment): void {
    this.isDownloading = true;
    this.wellAttachmentsService.openAttachment(attachment.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = attachment.fileName;
        anchor.click();
        URL.revokeObjectURL(url);
        this.isDownloading = false;
      },
      error: () => {
        this.isDownloading = false;
      }
    });
  }

  private editDescription(attachment: WellAttachment): void {
    const ref = this.dialog.open(EditDescriptionDialogComponent, {
      width: '520px',
      data: {
        attachmentId: attachment.id,
        fileName: attachment.fileName,
        description: attachment.description,
      }
    });

    ref.afterClosed().subscribe((result: EditDescriptionDialogResult | undefined) => {
      if (!result) return;
      this.wellAttachmentsService.updateDescription(result.attachmentId, result.description).subscribe({
        next: () => {
          const target = this.attachments.find(a => a.id === result.attachmentId);
          if (target) {
            target.description = result.description;
            this.attachments = [...this.attachments];
          }
        }
      });
    });
  }

  openUploadDialog(): void {
    const ref = this.dialog.open(UploadAttachmentsDialogComponent, {
      width: '760px',
      data: {
        wellId: this.data.wellId,
        wellName: this.data.wellName,
      }
    });

    ref.afterClosed().subscribe((uploaded: boolean | undefined) => {
      if (uploaded) {
        this.loadAttachments();
      }
    });
  }

  private deleteAttachment(attachment: WellAttachment): void {
    const ref = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Confirm Delete',
        message: `Delete "${attachment.fileName}"?`,
      }
    });

    ref.componentInstance.confirmRequested.subscribe(() => {
      ref.componentInstance.setDeleting(true);
      this.wellAttachmentsService.delete(attachment.wellID, attachment.id).subscribe({
        next: () => {
          this.attachments = this.attachments.filter(a => a.id !== attachment.id);
          ref.close(true);
        },
        error: () => {
          ref.componentInstance.setDeleting(false);
        }
      });
    });
  }

  private loadAttachments(): void {
    this.isLoading = true;
    this.wellAttachmentsService.getByWellId(this.data.wellId).subscribe({
      next: (attachments) => {
        this.attachments = attachments;
        this.isLoading = false;
      },
      error: () => {
        this.attachments = [];
        this.isLoading = false;
      }
    });
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
      formData.append('descriptions', '');
    });

    this.uploadError = '';
    this.isUploading = true;
    this.dialogRef.disableClose = true;

    this.wellAttachmentsService.upload(this.data.wellId, formData).subscribe({
      next: () => {
        this.isUploading = false;
        this.dialogRef.disableClose = false;
        this.loadAttachments();
      },
      error: () => {
        this.isUploading = false;
        this.dialogRef.disableClose = false;
        this.uploadError = 'Upload failed. Please try again.';
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
